import {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {searchWorkspace, suggestSearch, SEARCHABLE_COLLECTIONS} from '../services/search.js';
import {useWorkspace} from '../hooks/useWorkspace';
import {workspacePath} from '../services/paths.js';

const {
  FiSearch,
  FiBookmark,
  FiFileText,
  FiCheckSquare,
  FiFolder,
  FiMessageSquare,
  FiMail,
  FiLayers,
  FiArrowRight,
  FiX
} = FiIcons;

const COLLECTION_LABELS = {
  all: 'All',
  businessMemory: 'Memory',
  plans: 'Plans',
  tasks: 'Tasks',
  files: 'Files',
  chatMessages: 'Chat',
  inboxMessages: 'Inbox',
  adCampaignBriefs: 'Ads',
  projects: 'Projects'
};

function getCollectionIcon(collection) {
  switch (collection) {
    case 'businessMemory':
      return FiBookmark;
    case 'plans':
      return FiFileText;
    case 'tasks':
      return FiCheckSquare;
    case 'files':
      return FiFolder;
    case 'chatMessages':
      return FiMessageSquare;
    case 'inboxMessages':
      return FiMail;
    case 'adCampaignBriefs':
    case 'projects':
      return FiLayers;
    default:
      return FiFileText;
  }
}

export default function GlobalSearch({isOpen, onClose, onOpen}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef(null);
  const navigate = useNavigate();
  const state = useWorkspace();

  // Keyboard shortcut: Cmd+K / Ctrl+K or '/' to toggle search
  useEffect(() => {
    function handleKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (isOpen) {
          onClose();
        } else {
          onOpen();
        }
      } else if (event.key === '/' && !isOpen) {
        const activeTag = document.activeElement?.tagName?.toLowerCase();
        if (activeTag !== 'input' && activeTag !== 'textarea') {
          event.preventDefault();
          onOpen();
        }
      } else if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onOpen, onClose]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setSelectedIndex(0);
    } else {
      setQuery('');
      setResults([]);
      setSuggestions([]);
    }
  }, [isOpen]);

  // Debounced live search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      const collectionFilter = filter === 'all' ? null : filter;
      const hits = searchWorkspace(query, {
        collection: collectionFilter,
        limit: 20
      });
      setResults(hits);
      setSuggestions(suggestSearch(query, 4));
      setSelectedIndex(0);
    }, 120);

    return () => clearTimeout(timer);
  }, [query, filter]);

  function handleSelectResult(result) {
    if (!result) return;
    const dest = workspacePath(result.moduleRoute, state);
    onClose();
    navigate(dest);
  }

  function handleKeyDownInModal(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (results.length ? (prev + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (results.length ? (prev - 1 + results.length) % results.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (results[selectedIndex]) {
        handleSelectResult(results[selectedIndex]);
      }
    }
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        className="global-search-trigger"
        onClick={onOpen}
        title="Search workspace (⌘K or Ctrl+K)"
        aria-label="Search workspace"
      >
        <SafeIcon icon={FiSearch} />
        <span className="trigger-label">Search workspace…</span>
        <kbd className="trigger-kbd">⌘K</kbd>
      </button>
    );
  }

  return (
    <div className="search-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="search-modal-panel"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDownInModal}
        role="dialog"
        aria-modal="true"
        aria-label="Workspace search"
      >
        <div className="search-input-header">
          <SafeIcon icon={FiSearch} className="search-header-icon" />
          <input
            ref={inputRef}
            className="search-modal-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search memory, plans, tasks, files, messages…"
            aria-autocomplete="list"
          />
          {query && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={() => setQuery('')}
              aria-label="Clear query"
            >
              <SafeIcon icon={FiX} />
            </button>
          )}
          <kbd className="esc-chip" onClick={onClose}>ESC</kbd>
        </div>

        {/* Filter categories */}
        <div className="search-filter-chips">
          {['all', ...SEARCHABLE_COLLECTIONS].map((colKey) => (
            <button
              key={colKey}
              type="button"
              className={`filter-chip ${filter === colKey ? 'active' : ''}`}
              onClick={() => setFilter(colKey)}
            >
              {COLLECTION_LABELS[colKey] || colKey}
            </button>
          ))}
        </div>

        {/* Autocomplete suggestions */}
        {suggestions.length > 0 && query && (
          <div className="search-suggestions-row">
            <span className="sugg-label">Suggestions:</span>
            {suggestions.map((sug) => (
              <button
                key={sug}
                type="button"
                className="sugg-chip"
                onClick={() => setQuery(sug)}
              >
                {sug}
              </button>
            ))}
          </div>
        )}

        {/* Results Container */}
        <div className="search-results-list" role="listbox">
          {query.trim() && results.length === 0 ? (
            <div className="search-empty-state">
              <p>No results found for <b>“{query}”</b></p>
              <small>MiniSearch typo-tolerance searched across your local workspace. Try different terms or broaden the filter.</small>
            </div>
          ) : null}

          {!query.trim() ? (
            <div className="search-empty-prompt">
              <p>Search everything in your command center</p>
              <small>Search intentionally saved business memory facts, active plans, tasks, files, and chat history. Everything stays in your browser.</small>
            </div>
          ) : null}

          {results.map((hit, index) => {
            const IconComponent = getCollectionIcon(hit.collection);
            const isSelected = index === selectedIndex;
            return (
              <div
                key={hit.id}
                className={`search-result-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelectResult(hit)}
                onMouseEnter={() => setSelectedIndex(index)}
                role="option"
                aria-selected={isSelected}
              >
                <span className="result-type-icon">
                  <SafeIcon icon={IconComponent} />
                </span>

                <div className="result-body">
                  <div className="result-title-row">
                    <span className="result-title">{hit.title}</span>
                    <span className="result-collection-badge">
                      {COLLECTION_LABELS[hit.collection] || hit.collection}
                    </span>
                    {hit.status && (
                      <span className={`result-status-chip ${hit.status}`}>
                        {hit.status}
                      </span>
                    )}
                  </div>
                  <p className="result-snippet">{hit.text}</p>
                </div>

                <div className="result-action">
                  <span className="result-route-label">{hit.moduleRoute}</span>
                  <SafeIcon icon={FiArrowRight} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="search-footer">
          <span><kbd>↑</kbd> <kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
          <span className="search-brand">Local-first MiniSearch</span>
        </div>
      </div>
    </div>
  );
}
